import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { exportExcel, exportPDF, downloadJSON } from "@/lib/exporters";
import { Button } from "@/components/ui/button";
import { FileJson, Receipt, Layers, Hash } from "lucide-react";
import { devError } from "@/lib/logger";

function Stat({ label, value, accent }) {
  return (
    <div className="border border-gray-200 rounded bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-mono text-base font-semibold ${accent || "text-gray-900"}`}>{value}</div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded bg-blue-50 text-blue-700 flex items-center justify-center">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div>
        <div className="font-semibold text-gray-900 text-sm">{title}</div>
        {hint && <div className="text-[11px] text-gray-500">{hint}</div>}
      </div>
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/reports/gstr1`, { params })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => devError("GSTR-1 report failed", err));
    return () => { mounted = false; };
  }, [activeId, applied.start, applied.end, params]);

  const onExcel = () => {
    if (!data) return;
    const sheets = [];
    // Summary sheet
    sheets.push({
      name: "Summary",
      rows: [
        ["GSTR-1 Outward Supplies"],
        ["Company", data.company.name],
        ["GSTIN", data.company.gstin || "—"],
        ["State", `${data.company.state} (${data.company.state_code})`],
        ["Period", formatRangeLabel(range)],
        [],
        ["B2B invoices", data.summary.b2b_count],
        ["B2CS aggregates", data.summary.b2cs_count],
        ["CDNR notes", data.summary.cdnr_count],
        ["CDNUR notes", data.summary.cdnur_count],
        [],
        ["Total taxable value", data.summary.total_taxable],
        ["Total CGST", data.summary.total_cgst],
        ["Total SGST", data.summary.total_sgst],
        ["Total IGST", data.summary.total_igst],
        ["Total tax", data.summary.total_tax],
        ["Total invoice value", data.summary.total_invoice_value],
      ],
    });
    // B2B sheet
    const b2bRows = [
      ["GSTIN/UIN of Recipient", "Receiver Name", "Invoice Number", "Invoice Date", "Invoice Value", "Place Of Supply", "Reverse Charge", "Applicable % of Tax Rate", "Invoice Type", "Rate", "Taxable Value", "CGST", "SGST", "IGST", "Cess"],
    ];
    data.b2b.forEach((g) =>
      g.inv.forEach((iv) => {
        const it = iv.itms[0]?.itm_det || {};
        b2bRows.push([g.ctin, g.party_name, iv.inum, iv.idt, iv.val, iv.pos, iv.rchrg, "", iv.inv_typ, it.rt, it.txval, it.camt, it.samt, it.iamt, it.csamt || 0]);
      })
    );
    sheets.push({ name: "B2B", rows: b2bRows });

    // B2CS sheet
    const b2csRows = [["Type", "Place Of Supply", "Rate", "Taxable Value", "Cess Amount", "E-Commerce GSTIN"]];
    data.b2cs.forEach((r) => b2csRows.push([r.sply_ty === "INTER" ? "OE" : "OE", r.pos, r.rt, r.txval, r.csamt || 0, ""]));
    sheets.push({ name: "B2CS", rows: b2csRows });

    // CDNR sheet
    const cdnrRows = [["GSTIN/UIN of Recipient", "Receiver Name", "Note Number", "Note Date", "Note Type", "Place Of Supply", "Note Value", "Rate", "Taxable Value", "CGST", "SGST", "IGST", "Cess"]];
    data.cdnr.forEach((g) =>
      g.nt.forEach((n) => {
        const it = n.itms[0]?.itm_det || {};
        cdnrRows.push([g.ctin, g.party_name, n.nt_num, n.nt_dt, n.ntty, n.pos, n.val, it.rt, it.txval, it.camt, it.samt, it.iamt, it.csamt || 0]);
      })
    );
    sheets.push({ name: "CDNR", rows: cdnrRows });

    // HSN sheet
    const hsnRows = [["Sr. No.", "HSN", "UQC", "Total Quantity", "Rate", "Total Taxable Value", "Integrated Tax Amount", "Central Tax Amount", "State/UT Tax Amount", "Cess Amount", "Total Value"]];
    data.hsn.data.forEach((r) => hsnRows.push([r.num, r.hsn_sc, r.uqc, r.qty, r.rt, r.txval, r.iamt, r.camt, r.samt, r.csamt || 0, r.val]));
    sheets.push({ name: "HSN", rows: hsnRows });

    // Document summary
    sheets.push({
      name: "Docs",
      rows: [["Document Type", "From", "To", "Total", "Cancelled", "Net Issued"], ...data.doc_issue.map((d) => [d.doc_typ, d.from, d.to, d.totnum, d.cancel, d.net_issue])],
    });

    exportExcel(`gstr1-${active?.name || "report"}`, sheets);
  };

  const onPDF = () => {
    if (!data) return;
    const sections = [];
    sections.push({
      title: "Summary",
      headers: ["Section", "Count / Amount (₹)"],
      rows: [
        ["B2B invoices", String(data.summary.b2b_count)],
        ["B2CS aggregates", String(data.summary.b2cs_count)],
        ["CDNR (Credit/Debit notes — registered)", String(data.summary.cdnr_count)],
        ["CDNUR (Credit/Debit notes — unregistered)", String(data.summary.cdnur_count)],
        ["Total taxable value", formatINR(data.summary.total_taxable)],
        ["Total CGST", formatINR(data.summary.total_cgst)],
        ["Total SGST", formatINR(data.summary.total_sgst)],
        ["Total IGST", formatINR(data.summary.total_igst)],
        ["Total tax", formatINR(data.summary.total_tax)],
        ["Total invoice value", formatINR(data.summary.total_invoice_value)],
      ],
    });
    if (data.b2b.length) {
      const rows = [];
      data.b2b.forEach((g) =>
        g.inv.forEach((iv) => {
          const it = iv.itms[0]?.itm_det || {};
          rows.push([g.ctin, g.party_name, iv.inum, iv.idt, formatINR(iv.val), `${it.rt}%`, formatINR(it.txval), formatINR(it.iamt + it.camt + it.samt)]);
        })
      );
      sections.push({ title: "B2B", headers: ["GSTIN", "Party", "Invoice", "Date", "Value", "Rate", "Taxable", "Tax"], rows });
    }
    if (data.b2cs.length) {
      sections.push({ title: "B2CS", headers: ["POS", "Type", "Rate", "Taxable", "CGST", "SGST", "IGST"], rows: data.b2cs.map((r) => [r.pos, r.sply_ty, `${r.rt}%`, formatINR(r.txval), formatINR(r.camt), formatINR(r.samt), formatINR(r.iamt)]) });
    }
    if (data.hsn.data.length) {
      sections.push({ title: "HSN Summary", headers: ["#", "HSN", "UQC", "Qty", "Rate", "Taxable", "Tax", "Total"], rows: data.hsn.data.map((r) => [String(r.num), r.hsn_sc, r.uqc, formatINR(r.qty), `${r.rt}%`, formatINR(r.txval), formatINR(r.iamt + r.camt + r.samt), formatINR(r.val)]) });
    }
    exportPDF(`gstr1-${active?.name || "report"}`, `GSTR-1 — ${formatRangeLabel(range)}`, sections, { company: active?.name });
  };

  const onJSON = () => {
    if (!data) return;
    downloadJSON(`gstr1-${active?.name || "report"}`, {
      gstin: data.company.gstin,
      ret_period: data.period || (data.start ? data.start.slice(0, 7) : ""),
      gt: data.summary.total_invoice_value,
      cur_gt: data.summary.total_invoice_value,
      b2b: data.b2b,
      b2cs: data.b2cs,
      cdnr: data.cdnr,
      cdnur: data.cdnur,
      hsn: data.hsn,
      doc_issue: { doc_det: data.doc_issue },
      _meta: { generated_by: "AXIORA", company: data.company.name, period_start: data.start, period_end: data.end },
    });
  };

  return (
    <>
      <PageHeader
        title="GSTR-1 — Outward Supplies"
        subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onJSON} disabled={!data} className="h-8" data-testid="btn-export-json">
              <FileJson className="w-4 h-4 mr-1.5" /> JSON
            </Button>
            <ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />
          </div>
        }
      />
      <div className="p-6 space-y-6" data-testid="gstr1-page">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} />
        {!data && <div className="text-sm text-gray-500" data-testid="gstr1-loading">Loading…</div>}
        {data && (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="gstr1-summary">
              <Stat label="B2B" value={data.summary.b2b_count} />
              <Stat label="B2CS" value={data.summary.b2cs_count} />
              <Stat label="CDNR" value={data.summary.cdnr_count} />
              <Stat label="Taxable" value={`₹ ${formatINR(data.summary.total_taxable)}`} />
              <Stat label="Total Tax" value={`₹ ${formatINR(data.summary.total_tax)}`} accent="text-blue-700" />
              <Stat label="Invoice Value" value={`₹ ${formatINR(data.summary.total_invoice_value)}`} />
            </div>

            {/* B2B */}
            <section>
              <SectionHeader icon={Receipt} title={`B2B — Invoices to Registered Persons (${data.summary.b2b_count})`} hint="Buyer has a valid GSTIN" />
              {data.b2b.length === 0 ? (
                <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded p-3">No B2B invoices in this period.</div>
              ) : (
                <div className="border border-gray-200 rounded bg-white overflow-hidden">
                  <table className="tx-table min-w-[900px]">
                    <thead>
                      <tr>
                        <th>GSTIN</th><th>Party</th><th>Invoice</th><th>Date</th>
                        <th className="text-right">Taxable</th><th className="text-right">Rate</th>
                        <th className="text-right">CGST</th><th className="text-right">SGST</th><th className="text-right">IGST</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody data-testid="gstr1-b2b-rows">
                      {data.b2b.flatMap((g) =>
                        g.inv.map((iv) => {
                          const it = iv.itms[0]?.itm_det || {};
                          return (
                            <tr key={`${g.ctin}-${iv.inum}`}>
                              <td className="font-mono text-xs">{g.ctin}</td>
                              <td className="text-xs">{g.party_name}</td>
                              <td className="font-mono text-xs">{iv.inum}</td>
                              <td className="font-mono text-xs">{iv.idt}</td>
                              <td className="tx-num">{formatINR(it.txval)}</td>
                              <td className="tx-num">{it.rt}%</td>
                              <td className="tx-num">{formatINR(it.camt)}</td>
                              <td className="tx-num">{formatINR(it.samt)}</td>
                              <td className="tx-num">{formatINR(it.iamt)}</td>
                              <td className="tx-num font-semibold">{formatINR(iv.val)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* B2CS */}
            <section>
              <SectionHeader icon={Layers} title={`B2CS — B2C (Small) Aggregated by POS + Rate (${data.summary.b2cs_count})`} hint="Buyer has no GSTIN — aggregated per state + tax rate" />
              {data.b2cs.length === 0 ? (
                <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded p-3">No B2CS rows in this period.</div>
              ) : (
                <div className="border border-gray-200 rounded bg-white overflow-hidden">
                  <table className="tx-table">
                    <thead>
                      <tr>
                        <th>POS</th><th>Type</th><th className="text-right">Rate</th>
                        <th className="text-right">Taxable</th><th className="text-right">CGST</th>
                        <th className="text-right">SGST</th><th className="text-right">IGST</th>
                      </tr>
                    </thead>
                    <tbody data-testid="gstr1-b2cs-rows">
                      {data.b2cs.map((r) => (
                        <tr key={`b2cs-${r.pos}-${r.sply_ty}-${r.rt}`}>
                          <td className="font-mono text-xs">{r.pos}</td>
                          <td className="text-xs">{r.sply_ty}</td>
                          <td className="tx-num">{r.rt}%</td>
                          <td className="tx-num">{formatINR(r.txval)}</td>
                          <td className="tx-num">{formatINR(r.camt)}</td>
                          <td className="tx-num">{formatINR(r.samt)}</td>
                          <td className="tx-num">{formatINR(r.iamt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* CDNR + CDNUR */}
            <section>
              <SectionHeader icon={Receipt} title={`Credit Notes (CDNR + CDNUR: ${data.summary.cdnr_count + data.summary.cdnur_count})`} hint="Sales returns recorded via Credit Note vouchers" />
              {data.cdnr.length === 0 && data.cdnur.length === 0 ? (
                <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded p-3">No CDN entries in this period.</div>
              ) : (
                <div className="border border-gray-200 rounded bg-white overflow-hidden">
                  <table className="tx-table">
                    <thead>
                      <tr>
                        <th>Note No.</th><th>Date</th><th>Type</th><th>Party</th><th>GSTIN</th>
                        <th className="text-right">Taxable</th><th className="text-right">Tax</th>
                        <th className="text-right">Value</th>
                      </tr>
                    </thead>
                    <tbody data-testid="gstr1-cdn-rows">
                      {data.cdnr.flatMap((g) =>
                        g.nt.map((n) => {
                          const it = n.itms[0]?.itm_det || {};
                          return (
                            <tr key={`r-${g.ctin}-${n.nt_num}`}>
                              <td className="font-mono text-xs">{n.nt_num}</td>
                              <td className="font-mono text-xs">{n.nt_dt}</td>
                              <td className="text-xs">{n.ntty === "C" ? "Credit" : "Debit"}</td>
                              <td className="text-xs">{g.party_name}</td>
                              <td className="font-mono text-xs">{g.ctin}</td>
                              <td className="tx-num">{formatINR(it.txval)}</td>
                              <td className="tx-num">{formatINR(it.iamt + it.camt + it.samt)}</td>
                              <td className="tx-num font-semibold">{formatINR(n.val)}</td>
                            </tr>
                          );
                        })
                      )}
                      {data.cdnur.map((n, i) => {
                        const it = n.itms[0]?.itm_det || {};
                        return (
                          <tr key={`u-${i}`}>
                            <td className="font-mono text-xs">{n.nt_num}</td>
                            <td className="font-mono text-xs">{n.nt_dt}</td>
                            <td className="text-xs">{n.ntty === "C" ? "Credit" : "Debit"}</td>
                            <td className="text-xs">{n.party_name}</td>
                            <td className="font-mono text-xs text-gray-400">—</td>
                            <td className="tx-num">{formatINR(it.txval)}</td>
                            <td className="tx-num">{formatINR(it.iamt + it.camt + it.samt)}</td>
                            <td className="tx-num font-semibold">{formatINR(n.val)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* HSN */}
            <section>
              <SectionHeader icon={Hash} title={`HSN Summary (${data.hsn.data.length})`} hint="Consolidated by HSN/SAC + tax rate + unit" />
              {data.hsn.data.length === 0 ? (
                <div className="text-xs text-gray-400 border border-dashed border-gray-200 rounded p-3">No HSN data in this period.</div>
              ) : (
                <div className="border border-gray-200 rounded bg-white overflow-hidden">
                  <table className="tx-table">
                    <thead>
                      <tr>
                        <th>#</th><th>HSN/SAC</th><th>UQC</th>
                        <th className="text-right">Qty</th><th className="text-right">Rate</th>
                        <th className="text-right">Taxable</th><th className="text-right">CGST</th>
                        <th className="text-right">SGST</th><th className="text-right">IGST</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody data-testid="gstr1-hsn-rows">
                      {data.hsn.data.map((r) => (
                        <tr key={r.num}>
                          <td className="font-mono text-xs">{r.num}</td>
                          <td className="font-mono text-xs">{r.hsn_sc}</td>
                          <td className="text-xs">{r.uqc}</td>
                          <td className="tx-num">{formatINR(r.qty)}</td>
                          <td className="tx-num">{r.rt}%</td>
                          <td className="tx-num">{formatINR(r.txval)}</td>
                          <td className="tx-num">{formatINR(r.camt)}</td>
                          <td className="tx-num">{formatINR(r.samt)}</td>
                          <td className="tx-num">{formatINR(r.iamt)}</td>
                          <td className="tx-num font-semibold">{formatINR(r.val)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {data.doc_issue.length > 0 && (
              <section>
                <SectionHeader icon={Receipt} title="Document Summary" hint="Range of invoice / note numbers issued" />
                <div className="border border-gray-200 rounded bg-white overflow-hidden">
                  <table className="tx-table">
                    <thead><tr><th>Doc Type</th><th>From</th><th>To</th><th className="text-right">Issued</th><th className="text-right">Cancelled</th><th className="text-right">Net</th></tr></thead>
                    <tbody data-testid="gstr1-docs-rows">
                      {data.doc_issue.map((d) => (
                        <tr key={d.doc_typ}>
                          <td className="text-xs">{d.doc_typ}</td>
                          <td className="font-mono text-xs">{d.from}</td>
                          <td className="font-mono text-xs">{d.to}</td>
                          <td className="tx-num">{d.totnum}</td>
                          <td className="tx-num">{d.cancel}</td>
                          <td className="tx-num">{d.net_issue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default function GSTR1() {
  return (
    <RequireCompany>
      <Inner />
    </RequireCompany>
  );
}
